import React from 'react';
import Card from '../shared/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle, FiSun, FiCalendar, FiClock, FiCheckCircle } = FiIcons;

const PRIORITY_CONFIG = {
  immediate: { label: 'Immediate', icon: FiAlertCircle, cls: 'text-rose-600 bg-rose-50 border-rose-200' },
  today: { label: 'Today', icon: FiSun, cls: 'text-amber-600 bg-amber-50 border-amber-200' },
  this_week: { label: 'This Week', icon: FiCalendar, cls: 'text-sky-600 bg-sky-50 border-sky-200' },
  upcoming: { label: 'Upcoming', icon: FiClock, cls: 'text-gray-600 bg-gray-50 border-gray-200' },
};

const PRIORITY_ORDER = ['immediate', 'today', 'this_week', 'upcoming'];

const CONFIDENCE_STYLES = {
  high: 'text-emerald-600 bg-emerald-50',
  medium: 'text-amber-600 bg-amber-50',
  low: 'text-gray-500 bg-gray-100',
};

function confidenceLabel(confidence) {
  if (confidence >= 0.75) return 'high';
  if (confidence >= 0.4) return 'medium';
  return 'low';
}

// Placeholder items — shape matches PatientRecommendation in
// src/contracts/patientIntelligence.js. `explanation` is the plain-language
// "why am I seeing this?" rationale; `confidence` is 0-1 and rendered as
// High/Medium/Low per the explainability requirement. Once the unified
// patient_intelligence_profile exists, these should be generated from it
// rather than hardcoded here.
const PLACEHOLDER_ITEMS = [
  {
    id: 'r1',
    priority: 'immediate',
    text: 'Take your medication now',
    explanation: 'Your scheduled dose window opened 30 minutes ago and has not been logged.',
    confidence: 0.92,
  },
  {
    id: 'r2',
    priority: 'today',
    text: 'Drink more water',
    explanation: 'Your hydration levels have been below target for the last 2 days.',
    confidence: 0.8,
  },
  {
    id: 'r3',
    priority: 'this_week',
    text: 'Book your blood test',
    explanation: 'Your last lipid panel was over 6 months ago.',
    confidence: 0.7,
  },
  {
    id: 'r4',
    priority: 'upcoming',
    text: 'Annual diabetic review due in 8 days',
    explanation: 'Your annual review is scheduled based on your care plan.',
    confidence: 0.95,
  },
];

/**
 * Priority-grouped recommendations, each with a one-line explainability
 * note and a confidence indicator. Groups with no entries are not
 * rendered, so the card never shows an empty "Immediate" section.
 */
const RecommendationsCard = ({ items = PLACEHOLDER_ITEMS }) => {
  const grouped = PRIORITY_ORDER
    .map((key) => ({ key, config: PRIORITY_CONFIG[key], entries: items.filter((i) => i.priority === key) }))
    .filter((group) => group.entries.length > 0);

  return (
    <Card title="Recommendations">
      {grouped.length === 0 ? (
        <p className="text-sm text-gray-400">Nothing needs your attention right now.</p>
      ) : (
        <div className="space-y-5">
          {grouped.map((group) => (
            <div key={group.key}>
              <span
                className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border mb-2 ${group.config.cls}`}
              >
                <SafeIcon icon={group.config.icon} className="text-xs" /> {group.config.label}
              </span>
              <ul className="space-y-3">
                {group.entries.map((item) => {
                  const confLabel = confidenceLabel(item.confidence);
                  return (
                    <li key={item.id} className="border-l-2 border-gray-100 pl-3">
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex items-start gap-2 text-sm font-semibold text-gray-900">
                          <SafeIcon icon={FiCheckCircle} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                          {item.text}
                        </span>
                        <span
                          className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${CONFIDENCE_STYLES[confLabel]}`}
                        >
                          {confLabel}
                        </span>
                      </div>
                      {item.explanation && (
                        <p className="text-xs text-gray-400 mt-1 ml-6">
                          <span className="font-semibold text-gray-500">Why am I seeing this? </span>
                          {item.explanation}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-xs text-gray-400">
        Personalised recommendations will be generated by the AI Health Coach from unified
        Patient Intelligence Profile once connected.
      </p>
    </Card>
  );
};

export default RecommendationsCard;